import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureGrid,
  FeatureRow,
  StatBand,
  CTASection,
  Reveal,
  Mock,
} from './kit.jsx'

const OUTCOMES = [
  {
    icon: 'target',
    title: 'Fewer wasted hours',
    body: 'Reps open the app to a market that is already qualified — no scraping, no buying junk lists, no rebuilding spreadsheets every Monday.',
  },
  {
    icon: 'mail',
    title: 'Higher reply rates',
    body: 'Sequences send from your own Gmail as a real person, so your follow-ups land in the inbox and get answered instead of filtered.',
  },
  {
    icon: 'chart',
    title: 'Pipeline you can trust',
    body: 'Every company moves from new to won on a shared board, with reply and revenue signals rolled up so forecasts stop being guesswork.',
  },
  {
    icon: 'map',
    title: 'Territory that stays clean',
    body: 'Assign regions to reps and keep everyone on one source of truth — no double-touching accounts, no leads falling through the cracks.',
  },
  {
    icon: 'refresh',
    title: 'Faster ramp',
    body: 'A new hire is productive the same afternoon. Their region is loaded, the sequences are built, and the assistant answers the rest.',
  },
  {
    icon: 'sparkle',
    title: 'Answers on demand',
    body: 'Ask the in-app assistant which regions are converting or how outreach is pacing and get an answer straight from your live workspace.',
  },
]

const STATS = [
  { value: '10,942', label: 'Verified B2B leads' },
  { value: '13', label: 'Regions, ready on day one' },
  { value: '4', label: 'Outreach steps per sequence, on average' },
  { value: '1', label: 'Workspace for sourcing, outreach, and pipeline' },
]

export default function SolutionsPage() {
  return (
    <MarketingShell active="solutions">
      <Seo
        title="Solutions for vertical, services, and agency sales teams"
        description="Revailo Sales fits how you sell — whether you sell into a defined vertical like masonry contractors, sell services, pursue enterprise accounts, run outreach for agency clients, or expand into a new market."
        path="/solutions"
        keywords="vertical sales software, b2b leads, contractor leads, enterprise sales pipeline, outreach agency, market expansion"
      />

      <PageHero
        eyebrow="Solutions"
        title={
          <>
            Built for how your team <br className="max-[640px]:hidden" />
            actually win work.
          </>
        }
        lead="Vertical sellers, services businesses, enterprise sellers, agencies, and teams moving into new markets all sell differently. Revailo Sales shapes the same verified company database, outreach, and pipeline around each one — today's directory is masonry contractors."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Book a demo', to: '/demo' }}
      />

      <Section id="verticals">
        <Container>
          <Reveal>
            <FeatureRow
              eyebrow="Vertical sales teams"
              title="Reach the budget owner before your competitor does"
              body="When you sell into one trade — the current directory is masonry contractors — the first vendor to reach the owner or estimator usually wins the work. Revailo Sales hands you every qualified company in your region so you can work new logos, expansions, and competitive displacements from one enriched list."
              bullets={[
                'Every company in the database, filterable by region, size, and annual volume',
                'Spin up signal-triggered sequences and send them from your own Gmail',
                'Log every reply against the account so no hot lead goes cold',
              ]}
              media={
                <Reveal>
                  <Mock kind="leads" />
                </Reveal>
              }
            />
          </Reveal>
        </Container>
      </Section>

      <Section id="general" sm>
        <Container>
          <Reveal>
            <FeatureRow
              reverse
              eyebrow="Services businesses"
              title="Keep the pipeline full without the busywork"
              body="Selling services means keeping the pipeline full and the right accounts on speed dial. Start from the full database, filter to the industries and territories a quarter needs, and reach out in a few clicks instead of digging through a dozen spreadsheets."
              bullets={[
                'Every account, organized by industry and service area',
                'Source new accounts and expansion targets in the same workspace',
                'Track every opportunity from new to won on a shared deal-flow board',
              ]}
              media={
                <Reveal>
                  <Mock kind="sequence" />
                </Reveal>
              }
            />
          </Reveal>
        </Container>
      </Section>

      <Section id="commercial">
        <Container>
          <Reveal>
            <FeatureRow
              eyebrow="Commercial builders"
              title="Longer cycles, bigger accounts, full pipeline visibility"
              body="Commercial work turns on relationships that take months to mature. Revailo Sales keeps every large account and its named contacts in view, with a people-level funnel and multi-touch sequences so nothing stalls between the first intro and the signed contract."
              bullets={[
                'People-level pipeline built for multi-month sales cycles',
                'Nurture sequences with waits, follow-ups, and case-study touches',
                'Activity and Data views roll up which accounts and regions are moving',
              ]}
              media={
                <Reveal>
                  <Mock kind="analytics" />
                </Reveal>
              }
            />
          </Reveal>
        </Container>
      </Section>

      <Section id="agencies" sm>
        <Container>
          <Reveal>
            <FeatureRow
              reverse
              eyebrow="Agencies & lead resellers"
              title="Run outreach across every client from one seat"
              body="If you generate leads for other businesses, Revailo Sales is your delivery engine. Scope each client to their industry and region inside the 10,942-company database, build client-specific sequences that send from their Gmail, and prove results with dashboards you can hand straight to the client."
              bullets={[
                'Segment the database by client industry, region, and account size',
                'Send from each client’s own Gmail so replies stay with them',
                'Report reply rates and pipeline per client without exporting a thing',
              ]}
              media={
                <Reveal>
                  <Mock kind="inbox" />
                </Reveal>
              }
            />
          </Reveal>
        </Container>
      </Section>

      <Section id="expansion">
        <Container>
          <Reveal>
            <FeatureRow
              eyebrow="Regional expansion"
              title="Enter a new metro with the market already loaded"
              body="Opening a new territory usually means months of list-building before the first call. Skip it. Pick a new region and every qualified company is waiting — so your team is prospecting the market on day one instead of assembling it."
              bullets={[
                'Any of 50 U.S. regions loads in under a second',
                'Clone a proven sequence and point it at the new territory',
                'Compare regions side by side to see where to invest reps next',
              ]}
              media={
                <Reveal>
                  <Mock kind="dashboard" />
                </Reveal>
              }
            />
          </Reveal>
        </Container>
      </Section>

      <Section id="outcomes">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="What every team gets"
              title={<>Different plays, the same results.</>}
              lead="Whichever segment you sell into, Revailo Sales replaces the patchwork of lists, inboxes, and spreadsheets with one workspace built to move work forward."
            />
          </Reveal>
          <div className="mt-14">
            <Reveal>
              <FeatureGrid items={OUTCOMES} cols={3} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <StatBand stats={STATS} />
          </Reveal>
        </Container>
      </Section>

      <CTASection
        eyebrow="Solutions"
        title="Find your market. It's already in the database."
        body="Start free and see every qualified company in your region today — no scraping, no data project, no credit card."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Talk to sales', to: '/contact' }}
      />
    </MarketingShell>
  )
}
